import { useEffect, useState, useImperativeHandle, forwardRef  } from "react"

import { Switch } from "@mui/material";
import OpeningHoursPicker from "../../components/OpeningHoursPicker"
import PrepTimeSlider from "../../components/PrepTimeSlider"
import Modal from "../../components/Modal"
import IconButton from "../../components/IconButton";

import OrderingFulfillmentDetails from "../../containers/OrderingFulfillmentDetails";
import DeliveryServicePluginPicker from "../../containers/DeliveryServicePluginPicker"
import {
    PAYEE_CATALOG_TYPE_DELIVERY,
    PAYEE_CATALOG_TYPE_ORDERING,
} from "../../constants/payeeCatalog"
import { DEFAULT_PREP_TIMES, DEFAULT_PAYEE_OPENING_HOURS} from "../../constants/payeeCatalog";
import {useSelector, useDispatch} from "react-redux"
import { capitalizeAllWords } from "../../utils/stringUtils";
import PayeeAddressForm from "../PayeeAddressForm";

import { httpFetchSavePayeeCatalog } from "../../actions";
const ORDERING_INITIAL_VALUES = {
    openingHours: DEFAULT_PAYEE_OPENING_HOURS,
    fulfillment: {
        delivery: true,
        pickup: true
    },
    prepTimes: DEFAULT_PREP_TIMES,
    deliveryCatalogsById: {},
    location: {}
}

const OrderingCatalogForm = forwardRef(({
    config={},
    errors={},
    payeeId="",
    onChange=()=>{}
}, ref) => {
    const payee = useSelector(state => state.payees.payeesById[payeeId])
    const {defaultLocationId="", locationsById={}, name:payeeName=""} = payee || {}
    const payeeLocation = locationsById[defaultLocationId] || {}
    config = config || {}
    const values = typeof config === "object" && config[PAYEE_CATALOG_TYPE_ORDERING] ?
                                            config[PAYEE_CATALOG_TYPE_ORDERING] : 
                                            null
    //if the values are unset in the beginning, set default values
    useEffect(() => {
        if (!values){
            onChange({
                ...config,
                [PAYEE_CATALOG_TYPE_ORDERING] : {
                    ...ORDERING_INITIAL_VALUES,
                    // use the payee's default location as the location
                    location: {...payeeLocation, name: capitalizeAllWords(payeeName)}
                }
            })
        }
    }, [])

    const [addressModalOpen, setAddressModalOpen] = useState(false)
    const payeeCatalogsById = useSelector(state => state.payeeCatalogs.payeeCatalogsById)
    const validate = () => {
        if (typeof config !== "object" || !config[PAYEE_CATALOG_TYPE_ORDERING]) return {
                                                                                    success: false,
                                                                                    errors: {}
                                                                                }
        const errors = {}
        const {openingHours, fulfillment, location, prepTimes, deliveryCatalogsById} = config[PAYEE_CATALOG_TYPE_ORDERING]
        // validate opening hours
        if (!openingHours) errors.openingHours = "Opening hours is required"
        else {
            if (Object.keys(openingHours).length === 0) errors.openingHours = "Must open on at least one day"
        }
        //validate fulfillment
        const {delivery=false} = fulfillment || {}
        if (!fulfillment) errors.fulfillment = "Specify whether you support delivery and/or pickup"
        else {
            if (!fulfillment.pickup && !fulfillment.delivery) errors.fulfillment = "You must offer pickup or delivery or both"
        }
        //validate preptimes 
        if (!prepTimes) errors.prepTimes = "Preparation time is required"
        else {
            if (prepTimes.max >= "01:30") errors.prepTimes = "Worst case time must be less than 1 hr 30 mins"
        }
        //validate location
        const {settlementId="", line1="", directions="", name:locationName="", city=""} = location || {}
        if (!location) errors.location = "Location is required"
        else {
            if (!settlementId) errors.location = "Must specify the town or city"
            if (!line1 && !directions) errors.location = "Must specify line 1 or give directions"
            if (!locationName) errors.location = "Provide a name for the location"
        }
        //validate delivery service selection
        if (!deliveryCatalogsById) errors.deliveryCatalogsById = "Specify your delivery methods"
        else {
            //if delivery is specified as a method, but no delivery service is configured
            if ((Object.keys(deliveryCatalogsById).length === 0) && delivery) errors.deliveryCatalogsById = "Choose a delivery service"
            else if (location){
                const locationErrors = []
                Object.values(deliveryCatalogsById).forEach(d => {
                    const {name="", payeeName=""} = d
                    const deliveryCatalog = payeeCatalogsById[d.id]
                    if (!deliveryCatalog) locationErrors.push(`${capitalizeAllWords(name)} by ${payeeName} is not loaded`)
                    else {
                        const {basedInSettlementIds=[], deliveryPricesBySettlementId={}, handlesPickupsFromSettlementsNotBasedIn=false} = deliveryCatalog.config?.[PAYEE_CATALOG_TYPE_DELIVERY]
                        if (
                            !basedInSettlementIds.includes(settlementId) &&
                            !(
                                handlesPickupsFromSettlementsNotBasedIn &&
                                settlementId in deliveryPricesBySettlementId
                            )
                        ) locationErrors.push(`${capitalizeAllWords(name)} by ${payeeName} cannot pick up from ${capitalizeAllWords(city)}`)
                    }
                })
                if (locationErrors.length > 0 ) errors.deliveryCatalogsById = locationErrors[0]
            }
        }
        return {
            success: Object.keys(errors).length === 0,
            errors
        }
    }

    useImperativeHandle(ref, () => ({
        validate
    }));
    const {
        location={},
        openingHours={},
        prepTimes={},
        fulfillment={},
        deliveryCatalogsById={},
    } = values || {}
    //load any delivery catalos that are selected, but not loaded
    const dispatch = useDispatch()
    useEffect(() => {
        const deliveryCatalogIdsToLoad = Object.keys(deliveryCatalogsById)
                                             .filter(catalogId => !payeeCatalogsById[catalogId])
        deliveryCatalogIdsToLoad.forEach(catalogId => dispatch(httpFetchSavePayeeCatalog(catalogId)))
    }, [deliveryCatalogsById, payeeCatalogsById])
    if (!values) return null

    const handleChange = (value, key) => {
        const newConfig = {...config}
        //TODO validation here
        const newValues = {
            ...values,
            [key]: value
        }
        newConfig[PAYEE_CATALOG_TYPE_ORDERING] = newValues
        onChange(newConfig)
    }

    const handleChangeDeliveryCatalogs = selectedDeliveryCatalogsById => {
        const selected = {}
        Object.values(selectedDeliveryCatalogsById).forEach(c => {
            const {id, name="", handleId="", payeeId="", payeeName="", payeeLogoImageUrl="", payeeHandleId=""} = c
            selected[c.id] = {
                id,
                name,
                handleId,
                payeeId,
                payeeName,
                payeeLogoImageUrl,
                payeeHandleId
            }
        })
        handleChange(selected, "deliveryCatalogsById")
    }
    return (
        <div>
            <div className="h3 padding-05em">Ordering Settings</div>
            {/* location */}
            <div className="margin-bottom-5px">
                <div className="label">Where is your business located?</div>
                {errors.location ? <div className="errorText">{errors.location}</div> : null}
                <div className="display-flex">
                    <div className="flex-1">
                    <OrderingFulfillmentDetails 
                        location={location}
                    />
                    </div>
                    <div className="margin-left-05em">
                        <div>
                            <IconButton 
                                icon="Edit"
                                label="Edit"
                                onClick={() => setAddressModalOpen(true)}
                                buttonStyle={{
                                    width: 30,
                                    height: 30,
                                    backgroundColor: "var(--secondary)",
                                    color: "white",
                                }}
                                containerStyle={{color: "var(--secondary)"}}

                            />
                        </div>
                    </div>
                </div>
            </div>
            {/* opening hours */}
            <div className="margin-bottom-5px">
                <div className="label">What are your opening hours for orders?</div>
                {errors.openingHours ? <div className="errorText">{errors.openingHours}</div> : null}
                <OpeningHoursPicker 
                    openingHours={openingHours} 
                    onChange={openingHours => handleChange(openingHours, "openingHours")}
                />
            </div>
            {/* prep times */}
            <div className="margin-bottom-5px">
                <div className="label">How quickly will you prepare orders?</div>
                {errors.prepTimes? <div className="errorText">{errors.prepTimes}</div> : null}
                <PrepTimeSlider 
                    prepTimes={prepTimes}
                    onChange={prepTimes => handleChange(prepTimes, "prepTimes")}
                />
            </div>
            {/* fulfillment */}
            <div className="label">Will you offer delivery, pickup, or both?</div>
            {errors.fulfillment? <div className="errorText">{errors.fulfillment}</div> : null}
            <div className="label display-flex align-items-center">
                <Switch 
                    checked={fulfillment.delivery}
                    color="success"
                    onChange={() => handleChange({...fulfillment, delivery: !fulfillment.delivery}, "fulfillment")}
                />
                <div>
                    {fulfillment.delivery? "Delivery available" : "No delivery"} 
                </div>
            </div>
            <div className="label display-flex align-items-center">
                <Switch 
                    checked={fulfillment.pickup}
                    color="success"
                    onChange={() => handleChange({...fulfillment, pickup: !fulfillment.pickup}, "fulfillment")}
                />
                <div>
                    {fulfillment.pickup? "Pickup available" : "No pickup"} 
                </div>
            </div>
            {/* delivery catalogs */}
            <div className="label">Set up a delivery service</div>
            {errors.deliveryCatalogsById? <div className="errorText">{errors.deliveryCatalogsById}</div> : null}
            <DeliveryServicePluginPicker 
                location={location}
                title=""
                selectedDeliveryCatalogsById={deliveryCatalogsById}
                onChange={deliveryCatalogsById => handleChangeDeliveryCatalogs(deliveryCatalogsById)}
            />
            {
                addressModalOpen ?
                <Modal
                    isOpen={addressModalOpen}
                    closeModal={() => setAddressModalOpen(false)}
                    className="height-fit-content"
                    overlayClassName="flex-center"
                    closeOnOverlayClick={true}
                    showCloseButton={false}
                >
                    <PayeeAddressForm 
                        address={location}
                        formTitle="Business Address"
                        buttonText={"Save Address"}
                        postSubmit={() => setAddressModalOpen(false)} 
                        submit={location => handleChange(location, "location")}
                    />
                </Modal>
                :
                null
            }
        </div>
    )
})

export default OrderingCatalogForm